// Tiered pricing editor: one rate input ($/sq-mi) per canonical area bucket.
// Controlled — `tiers` is the 4-bucket array and `onChange` receives the next
// array. The bucket bounds are fixed by the server (admin-defined buckets are a
// stretch goal), so admins only edit the rate for each [min, max) area band.
import { useState } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Stack,
} from '@mui/material';
import { CUSTOM_QUOTE_MIN_SQ_MI, bucketLabel } from '../../config/pricing-buckets.js';

const TieredPricingEditor = ({ tiers, onChange, label = 'Pricing ($ per sq mi)' }) => {
  // Track what the admin is typing per field so a cleared input can stay empty
  // (the parent state coerces an empty rate to 0, which would otherwise snap a
  // deleted "0" right back into the box).
  const [drafts, setDrafts] = useState({});

  const setRate = (index) => (event) => {
    const raw = event.target.value;
    setDrafts((prev) => ({ ...prev, [index]: raw }));
    const rate = raw === '' ? 0 : Number(raw);
    onChange(tiers.map((tier, i) => (i === index ? { ...tier, rate } : tier)));
  };

  const commitRate = (index) => () => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const displayValue = (tier, index) => {
    if (index in drafts) return drafts[index];
    return Number.isFinite(tier.rate) ? tier.rate : 0;
  };

  return (
    <Box>
      <Typography component="span" variant="subtitle2" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Rate per square mile for each area band. Areas under
        {' '}
        {CUSTOM_QUOTE_MIN_SQ_MI}
        {' '}
        sq mi are quoted manually.
      </Typography>
      <Stack spacing={1.5}>
        {tiers.map((tier, index) => (
          <TextField
            key={tier.min}
            type="number"
            size="small"
            label={`${bucketLabel(tier)} sq mi`}
            value={displayValue(tier, index)}
            onChange={setRate(index)}
            onBlur={commitRate(index)}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              },
              htmlInput: { min: 0, step: 1 },
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default TieredPricingEditor;
